import { legSlice } from './slice';
import { store } from '@reduxStore';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, MutationActionCreatorResult, MutationDefinition } from '@reduxjs/toolkit/query';
import { FormFieldError, getApiErrorMessage, getApiFormFieldErrors } from '@api/getApiError';
import {
  BatchLegRequest,
  Leg,
  MutatableLegRaw,
  PatchLegPayload,
  PutLegRequest,
  PutLegResponse,
  updateLegApiCache,
  updateLegConsignmentCache
} from './types';
import { isObjectNullOrEmpty } from '@/Infrastructure/Utilities/object.type';
import { clearLegFeedback, setLegLoading } from '@dispatchBoard/dispatchBoardSlice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getTerminalNumber } from '@hooks/useCurrentTerminal';
import { consignmentSlice } from '@api/Trinium/Endpoints/Consignment/slice';
import { upsertLegInDispatchCache } from '../Dispatch/types';
import { dispatchSlice } from '../Dispatch/slice';
import { legToDispatchLeg } from '@api/Trinium/maps';
import { confirmAsync } from '@components/Modals/ConfirmPopup';

/**
 * Action to manually execute a Batch Put/Patch against the Leg API
 */
export async function batchLegRequest(
  requests: BatchLegRequest[] | Omit<PutLegRequest, 'terminalNumber'>[],
  // onErrorOverride available to handle errors when request is not called from Dispatch Board
  onErrorOverride?: (error: string) => void
) {
  const terminalNumber = getTerminalNumber();

  /** Track the responses and errors */
  const responses: LegMutation[] = [];

  /** Execute the patch request(s) */
  requests.forEach((leg) => {
    let request: LegMutation;
    store.dispatch(setLegLoading(leg.id));

    // Typeguard to ensure we post the correct type to the correct api
    if (isPutRequest(leg)) {
      request = store.dispatch(
        legSlice.endpoints.putLeg.initiate({
          terminalNumber: terminalNumber,
          ...leg
        })
      );
    } else {
      const { optimisticProperties, ...payload } = leg;
      request = store.dispatch(
        legSlice.endpoints.patchLeg.initiate({
          terminalNumber: terminalNumber,
          leg: {
            ...payload
          },
          optimistic: true,
          optimisticExtensions: optimisticProperties
        })
      );
    }

    request
      .unwrap()
      .catch((exception: unknown) => {
        const deserializedError = getApiErrorMessage(exception);
        if (onErrorOverride) {
          onErrorOverride(deserializedError);
        } else {
          store.dispatch(appendErrorAnnouncement({ message: deserializedError }));
        }
      })
      .finally(() => {
        store.dispatch(clearLegFeedback(leg.id));
      });

    responses.push(request);
  });

  await Promise.all(responses);
}

/** Local method to determine which type of request we are processing */
function isPutRequest(request: PatchLegPayload | Omit<PutLegRequest, 'terminalNumber'>): request is Omit<PutLegRequest, 'terminalNumber'> {
  return 'actionType' in (request as PutLegRequest);
}

/**
 * Determines whether a container/chassis change should be copied to all applicable legs and the order,
 * asking the user up front - before anything is sent to the server - when the terminal doesn't already
 * auto-duplicate changes by leg type. Returns undefined when no answer is needed (live loads and
 * auto-duplicating terminals let the backend decide on its own; clearing a value never prompts), leaving
 * `cpyCntrChassisToAllLegs` unset so the backend defaults to updating the leg only.
 */
export async function resolveCopyCntrChassisToAllLegs(
  isLive: boolean | undefined,
  newValue: string | null | undefined,
  previousValue: string | null | undefined,
  /** Which number(s) the change is to, named in the question */
  changedNumbers: 'container number' | 'chassis number' | 'container and chassis numbers'
): Promise<boolean | undefined> {
  if (isLive || !newValue?.trim()) {
    return undefined;
  }

  const terminal = store.getState().applicationState.selectedTerminal;
  if (!terminal?.promptForCntrChassisDup) {
    return undefined;
  }

  if (!previousValue?.trim()) {
    return true;
  }

  return await confirmAsync(`Do you wish to update the ${changedNumbers} on all legs?`);
}

/**
 * Asks up front - before the leg is removed - whether the leg's unapproved driver pay should be deleted, matching the
 * legacy remove leg question. Either answer still removes the leg; No keeps the pay on the order without a leg.
 * Returns undefined when the leg has no unapproved pay, leaving `deleteDriverPayments` off the request.
 */
export async function resolveDeleteDriverPayments(terminalNumber: string, legId: number): Promise<boolean | undefined> {
  // Re-read the leg since pay can change without refreshing the leg cache.
  const legRequest = store.dispatch(legSlice.endpoints.fetchLeg.initiate({ terminalNumber, id: legId }, { forceRefetch: true }));
  // A failed refetch still resolves with the last cached leg, if any.
  const { data: leg } = await legRequest;
  legRequest.unsubscribe();

  if (!leg?.hasUnapprovedPay) {
    return undefined;
  }

  return await confirmAsync('Do you wish to delete the associated Driver Payments?');
}

/** Joining type of Put and Patch Responses */
type LegMutation = PatchLegRtkResponse | PutLegRtkResponse;

/** Response type of the Patch Leg RTK Api */
type PatchLegRtkResponse = MutationActionCreatorResult<
  MutationDefinition<
    {
      terminalNumber: string;
      leg: PatchLegPayload;
      optimistic?: boolean;
    },
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    never,
    Leg[],
    'triniumApi'
  >
>;

/** Response type of the Put Leg RTK Api */
type PutLegRtkResponse = MutationActionCreatorResult<
  MutationDefinition<PutLegRequest, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, PutLegResponse, 'triniumApi'>
>;

interface SendLegMutationResponse {
  /**
   * If at least one API call was successfully made, this is the Leg Response received
   */
  updatedLegs: Leg[] | null;
}

export interface SendLegMutationErrorResponse extends Error {
  /**
   * If at least one API call was successfully made, this is the Leg Response received
   */
  updatedLegs: Leg[] | null;
  /**
   * Collection of any errors that occurred across all the API calls involved in the save
   */
  errors: FormFieldError[];
}

/**
 *
 * @param legMutations Pending changed fields that need to be updated
 * @param formMap If posting from a form, Map from API field names to relative form Field Names for wiring up errors messages to inputs
 * @param lastFreeDay This is a consignment value, but used in the Available PUT against the Leg API
 * @returns
 */
export async function sendLegMutations(
  legMutations: Pick<MutatableLegRaw, 'id'> &
    Partial<MutatableLegRaw> & {
      /** Pre-resolved answer to the leg-only/all-legs question (see resolveCopyCntrChassisToAllLegs), when the mutation changes cntrId or chassisId */
      cpyCntrChassisToAllLegs?: boolean;
    },
  formMap?: Record<string, string | Record<string, string>>,
  ignoreSubformNesting?: boolean,
  /**
   * When true, skip the local consignment-cache stamping. The caller is expected to refetch the consignment
   * from the server instead, letting the backend cascade be the source of truth rather than guessing which
   * leg fields propagate to the consignment header. Leg and dispatch-board caches are still updated locally.
   */
  skipConsignmentCacheUpdate?: boolean
) {
  /**
   * Deconstruct any properties that specifically need to be used in a PUT action
   */
  const { id, drvId, tractorId, fullAvailDate, emptyAvailDate, lastFreeDate, cutoffDate, cpyCntrChassisToAllLegs, ...pendingMutations } =
    legMutations;

  /** Because we are making multiple calls, the final call will be the response obj */
  let legResponse: Leg[] = [];

  /** Tracks the canApprove flag returned by assignResources so the consignment cache can be patched after all mutations resolve */
  let canApproveUpdate: boolean | undefined;

  const errors: FormFieldError[] = [];

  if (id) {
    store.dispatch(setLegLoading(id));

    const terminalNumber = getTerminalNumber();

    /**
     * Handle Availability PUT if needed
     */
    if (fullAvailDate !== undefined || emptyAvailDate !== undefined || lastFreeDate !== undefined || cutoffDate !== undefined) {
      const availablePutRequest: PutLegRequest = {
        terminalNumber: terminalNumber,
        id: id,
        actionType: {
          action: 'setAvailability',
          data: {
            availDate: fullAvailDate !== undefined ? fullAvailDate : emptyAvailDate,
            lastFreeDate: lastFreeDate,
            cutoffDate: cutoffDate
          }
        },
        skipCacheUpdate: true
      };

      const availableRequest = store.dispatch(legSlice.endpoints.putLeg.initiate(availablePutRequest));

      const availableResponse = await availableRequest
        .unwrap()
        .then((availableResponse) => {
          return availableResponse;
        })
        .catch((ex: unknown) => {
          if (formMap) {
            errors.push(...getApiFormFieldErrors(formMap, ex, ignoreSubformNesting));
          } else {
            errors.push({
              errorMessage: getApiErrorMessage(ex)
            });
          }
        });

      if (typeof availableResponse === 'object') {
        legResponse = availableResponse.legs;
      }
    }

    /**
     * Handle Driver PUT if needed
     */
    if (drvId) {
      const driverPutRequest: PutLegRequest = {
        terminalNumber: terminalNumber,
        id: id,
        actionType: {
          action: 'assignResources',
          data: {
            drvId: drvId,
            tractorId: tractorId ?? undefined
          }
        },
        skipCacheUpdate: true
      };

      const driverRequest = store.dispatch(legSlice.endpoints.putLeg.initiate(driverPutRequest));

      const driverResponse = await driverRequest
        .unwrap()
        .then((driverResponse) => {
          return driverResponse;
        })
        .catch((ex: unknown) => {
          if (formMap) {
            errors.push(...getApiFormFieldErrors(formMap, ex, ignoreSubformNesting));
          } else {
            errors.push({
              errorMessage: getApiErrorMessage(ex)
            });
          }
        });

      if (typeof driverResponse === 'object') {
        legResponse = driverResponse.legs;
        canApproveUpdate = driverResponse.canApprove;
      }
    } else if (drvId === null) {
      /* Removing the driver is an 'undo' action. Capture the response so the Consignment cache reflects the driver removal. */
      const undoRequest = store.dispatch(
        legSlice.endpoints.putLeg.initiate({
          terminalNumber: terminalNumber,
          id: id,
          actionType: {
            action: 'undo'
          },
          skipCacheUpdate: true
        })
      );

      const undoResponse = await undoRequest
        .unwrap()
        .then((undoResponse) => {
          return undoResponse;
        })
        .catch((ex: unknown) => {
          if (formMap) {
            errors.push(...getApiFormFieldErrors(formMap, ex, ignoreSubformNesting));
          } else {
            errors.push({
              errorMessage: getApiErrorMessage(ex)
            });
          }
        });

      if (typeof undoResponse === 'object') {
        legResponse = undoResponse.legs;
      }
    }

    /**
     * Handle the remaining PATCH properties if any
     */
    if (!isObjectNullOrEmpty(pendingMutations)) {
      const patchRequest: PatchLegPayload = {
        id: id,
        ...pendingMutations,
        ...(cpyCntrChassisToAllLegs !== undefined && { cpyCntrChassisToAllLegs })
      };

      const legRequest = store.dispatch(
        legSlice.endpoints.patchLeg.initiate({
          terminalNumber: terminalNumber,
          leg: patchRequest,
          optimistic: false,
          skipCacheUpdate: true
        })
      );

      const response = await legRequest
        .unwrap()
        .then((patchResponse) => {
          return patchResponse;
        })
        .catch((ex: unknown) => {
          if (formMap) {
            errors.push(...getApiFormFieldErrors(formMap, ex, ignoreSubformNesting));
          } else {
            errors.push({
              errorMessage: getApiErrorMessage(ex)
            });
          }
        });

      if (typeof response === 'object') {
        legResponse = response;
      }
    }

    store.dispatch(clearLegFeedback(id));

    // Perform a single cache update with the final API response to prevent intermediate re-renders
    // Avoiding the update if any of the APIs error which will wipe unsaved state, user needs to retry after correcting the error
    if (errors.length === 0) {
      // A leg type change can shift which leg is the first FCL / last ECL on a consignment, and we
      // can't determine that locally without the full leg set. Invalidate dispatch queries to refetch.
      const legTypeChanged = pendingMutations.type !== undefined;

      legResponse.forEach((leg) => {
        updateLegApiCache(leg);
        if (!skipConsignmentCacheUpdate) {
          updateLegConsignmentCache(leg);
        }
        if (!legTypeChanged) {
          upsertLegInDispatchCache(legToDispatchLeg(leg));
        }
      });

      if (legTypeChanged) {
        store.dispatch(dispatchSlice.util.invalidateTags(['dispatch', 'dispatch-by-job', 'dispatch-by-search']));
      }

      /**
       * If assignResources returned a canApprove flag, patch the consignment cache so the Approve button
       * enables/disables without requiring a refresh. The skipCacheUpdate flag on the putLeg call above
       * suppresses the equivalent update in onQueryStarted, so we need to apply it here.
       */
      if (canApproveUpdate !== undefined) {
        const csnId = legResponse[0]?.csnId;
        if (csnId) {
          store.dispatch(
            consignmentSlice.util.updateQueryData('fetchConsignment', { consignmentId: csnId, terminalNumber: terminalNumber }, (draft) => {
              draft.canApprove = canApproveUpdate;
            })
          );
        }
      }
    }
  }

  return await new Promise<SendLegMutationResponse>((resolve, reject) => {
    /**
     *	If any errors were returned, reject the promise with those errors
     */
    if (errors.length > 0) {
      const error: SendLegMutationErrorResponse = Object.assign(new Error('Leg mutation failed'), {
        updatedLegs: legResponse,
        errors: errors
      });
      reject(error);
      return;
    }

    /**
     * Return the most up to date leg response we received
     */
    resolve({
      updatedLegs: legResponse
    });
  });
}
